<template>
    <div class="min-h-screen">
        <div class="max-w-7xl mx-auto p-4 sm:p-6 md:p-8">
            <header class="mb-8">
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 class="text-3xl sm:text-4xl font-bold tracking-tight text-slate-800 dark:text-slate-100">
                            Meus <span
                                class="bg-clip-text text-transparent bg-gradient-to-r from-brand-500 to-brand-400">Investimentos</span>
                        </h1>
                        <p class="mt-2 text-sm text-slate-600 dark:text-slate-400">
                            Acompanhe a evolução do seu patrimônio em um só lugar.
                        </p>
                    </div>
                    <div class="flex-shrink-0">
                        <button @click="handleNovoInvestimento"
                            class="btn-primary w-full sm:w-auto flex items-center justify-center">
                            <span class="material-symbols-outlined mr-2 text-lg">add_circle</span>
                            Adicionar Investimento
                        </button>
                    </div>
                </div>
            </header>

            <div v-if="sortedInvestimentos.length > 0" class="mb-8">
                <div
                    class="bg-gradient-to-r from-brand-600 to-brand-700 text-white p-6 rounded-xl shadow-lg flex flex-col sm:flex-row justify-between items-center">
                    <div class="mb-2 sm:mb-0 text-center sm:text-left">
                        <span class="text-base sm:text-lg font-medium">Patrimônio Total Investido</span>
                    </div>
                    <div class="flex items-center">
                        <span class="text-2xl sm:text-3xl font-semibold tracking-tight">{{
                            displayCurrency(totalInvestimentos) }}</span>
                        <button @click="toggleVisibility"
                            class="ml-2 sm:ml-3 text-white/80 hover:text-white p-2 rounded-full transition-colors"
                            :title="showValues ? 'Ocultar valores' : 'Mostrar valores'"
                            aria-label="Alternar visibilidade dos valores">
                            <span v-if="showValues"
                                class="material-symbols-outlined text-xl sm:text-2xl">visibility</span>
                            <span v-else class="material-symbols-outlined text-xl sm:text-2xl">visibility_off</span>
                        </button>
                    </div>
                </div>
            </div>

            <div v-if="sortedInvestimentos.length === 0" class="empty-state">
                <span class="material-symbols-outlined text-6xl text-brand-400 dark:text-brand-700">savings</span>
                <p class="mt-4 font-medium">Nenhum investimento cadastrado.</p>
                <p class="text-sm">Adicione um ativo para começar a acompanhar.</p>
            </div>

            <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <div v-for="item in sortedInvestimentos" :key="item.id" class="card-investment">
                    <div class="p-4 flex items-center rounded-t-2xl" :class="getInvestmentTheme(item.tipo).bg">
                        <div class="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                            <span class="material-symbols-outlined text-xl"
                                :class="getInvestmentTheme(item.tipo).text_contrast">
                                {{ getIconForTipo(item.tipo) }}
                            </span>
                        </div>
                        <h3 class="font-bold text-lg ml-3 truncate" :class="getInvestmentTheme(item.tipo).text_contrast"
                            :title="item.descricao">
                            {{ item.descricao }}
                        </h3>
                    </div>

                    <div class="p-5 flex-grow">
                        <div class="flex justify-between items-baseline">
                            <div>
                                <p class="text-sm text-slate-500 dark:text-slate-400">Valor Atual</p>
                                <p class="text-3xl font-bold text-slate-700 dark:text-slate-200 tracking-tight">
                                    {{ displayCurrency(item.valorAtual, false) }}
                                </p>
                            </div>
                            <span v-if="showValues && totalInvestimentos > 0"
                                class="text-sm font-semibold text-brand-600 dark:text-brand-400 bg-brand-500/10 dark:bg-brand-900/50 px-2 py-1 rounded-md">
                                {{ calculatePercentage(item.valorAtual) }}
                            </span>
                        </div>

                        <div class="mt-4 pt-3 border-t border-slate-200 dark:border-brand-700/50 space-y-1.5 text-sm">
                            <div class="flex justify-between items-center text-slate-500 dark:text-slate-400">
                                <span>Tipo:</span>
                                <span class="font-semibold text-slate-700 dark:text-slate-200">{{
                                    getTipoLabel(item.tipo) }}</span>
                            </div>
                            <div class="flex justify-between items-center text-slate-500 dark:text-slate-400">
                                <span>Instituição:</span>
                                <span class="font-semibold text-slate-700 dark:text-slate-200 truncate"
                                    :title="item.instituicao">
                                    {{ item.instituicao || '--' }}
                                </span>
                            </div>
                            <div v-if="item.ticker"
                                class="flex justify-between items-center text-slate-500 dark:text-slate-400">
                                <span>Ticker:</span>
                                <span
                                    class="font-semibold text-slate-700 dark:text-slate-200 font-mono text-xs uppercase">{{
                                    item.ticker }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="card-footer">
                        <button @click="handleEditInvestimento(item)" title="Editar"
                            class="btn-action-icon text-sky-600 dark:text-sky-400 hover:bg-sky-100 dark:hover:bg-slate-700">
                            <span class="material-symbols-outlined text-lg">edit</span>
                        </button>
                        <button @click="confirmDelete(item.id)" title="Excluir"
                            class="btn-action-icon text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-slate-700">
                            <span class="material-symbols-outlined text-lg">delete</span>
                        </button>
                    </div>
                </div>
            </div>

            <transition name="modal-fade">
                <div v-if="showModal" @click.self="closeModal" class="modal-backdrop">
                    <div class="modal-card">
                        <header class="modal-header">
                            <h3 class="text-xl font-semibold">{{ isEditing ? 'Editar Investimento' : 'Adicionar Investimento' }}</h3>
                            <button @click="closeModal" title="Fechar" :disabled="isSubmitting" class="modal-close-btn">
                                <span class="material-symbols-outlined">close</span>
                            </button>
                        </header>
                        <form @submit.prevent="handleSubmit" class="p-6 space-y-4">
                            <div>
                                <label for="invDescricaoModal" class="form-label">Descrição</label>
                                <input id="invDescricaoModal" v-model="investimento.descricao" type="text" required
                                    placeholder="Ex: Ações Petrobras" class="form-input" />
                            </div>
                            <div>
                                <label for="invTipoModal" class="form-label">Tipo</label>
                                <select id="invTipoModal" v-model="investimento.tipo" required class="form-input">
                                    <option disabled value="">Selecione um tipo</option>
                                    <option v-for="tipo in tiposInvestimento" :key="tipo.value" :value="tipo.value">{{
                                        tipo.label }}</option>
                                </select>
                            </div>
                            <div v-if="showTickerInput" class="transition-all duration-300">
                                <label for="invTickerModal" class="form-label">Ticker</label>
                                <input id="invTickerModal" v-model="investimento.ticker" type="text"
                                    placeholder="Ex: PETR4" class="form-input uppercase" />
                            </div>
                            <div>
                                <label for="invInstituicaoModal" class="form-label">Instituição / Corretora</label>
                                <input id="invInstituicaoModal" v-model="investimento.instituicao" type="text"
                                    placeholder="Ex: XP Investimentos" class="form-input" />
                            </div>
                            <div>
                                <label for="invValorModal" class="form-label">Valor Atual (R$)</label>
                                <input id="invValorModal" v-model.number="investimento.valorAtual" type="number"
                                    step="0.01" required placeholder="0.00" class="form-input" />
                            </div>
                            <footer class="modal-footer !pt-4">
                                <button @click="closeModal" type="button" :disabled="isSubmitting"
                                    class="btn-secondary">Cancelar</button>
                                <button type="submit" :disabled="isSubmitting" class="btn-primary flex items-center">
                                    <span v-if="isSubmitting"
                                        class="material-symbols-outlined animate-spin mr-2 text-lg">progress_activity</span>
                                    {{ isSubmitting ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Adicionar') }}
                                </button>
                            </footer>
                        </form>
                    </div>
                </div>
            </transition>

            <transition name="modal-fade">
                <div v-if="showDeleteConfirm" @click.self="showDeleteConfirm = false" class="modal-backdrop">
                    <div class="modal-card p-0 max-w-md">
                        <div class="p-6 text-center">
                            <span
                                class="material-symbols-outlined text-5xl text-red-500 dark:text-red-400 mx-auto">warning</span>
                            <h3 class="modal-title">Excluir Investimento</h3>
                            <p class="modal-text">Tem certeza que deseja excluir este investimento?</p>
                        </div>
                        <div class="modal-footer"><button @click="showDeleteConfirm = false" type="button"
                                class="btn-secondary">Cancelar</button><button @click="executeDelete"
                                class="btn-danger">Sim, Excluir</button></div>
                    </div>
                </div>
            </transition>

            <transition name="modal-fade">
                <div v-if="showUnsavedChangesConfirm" @click.self="showUnsavedChangesConfirm = false"
                    class="modal-backdrop">
                    <div class="modal-card p-0 max-w-md">
                        <div class="p-6 text-center">
                            <span
                                class="material-symbols-outlined text-5xl text-amber-500 dark:text-amber-400 mx-auto">warning</span>
                            <h3 class="modal-title">Descartar Alterações?</h3>
                            <p class="modal-text">Você possui alterações não salvas. Tem certeza que deseja sair?</p>
                        </div>
                        <div class="modal-footer"><button @click="showUnsavedChangesConfirm = false" type="button"
                                class="btn-secondary">Cancelar</button><button @click="handleConfirmClose"
                                class="btn-danger">Sim, Descartar</button></div>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';

const router = useRouter();
const investimentos = ref([]);
const showModal = ref(false);
const isEditing = ref(false);
const isSubmitting = ref(false);
const showDeleteConfirm = ref(false);
const showUnsavedChangesConfirm = ref(false);
const itemParaDeletar = ref(null);
const investimento = ref(null);
const pristineInvestimento = ref(null);
const showValues = ref(true);

const isMobile = ref(false);
const checkDevice = () => {
    isMobile.value = window.innerWidth < 640;
};

const apiUrl = 'https://backend.controlease.com.br/investimentos';
const investmentThemes = {
    RENDA_FIXA: { bg: 'bg-sky-600', text_contrast: 'text-white' },
    RENDA_VARIAVEL: { bg: 'bg-emerald-600', text_contrast: 'text-white' },
    FUNDO_IMOBILIARIO: { bg: 'bg-amber-600', text_contrast: 'text-white' },
    TESOURO_DIRETO: { bg: 'bg-brand-600', text_contrast: 'text-white' },
    CRIPTOATIVO: { bg: 'bg-slate-700', text_contrast: 'text-white' },
};
const tiposInvestimento = ref([
    { value: 'RENDA_FIXA', label: 'Renda Fixa' },
    { value: 'RENDA_VARIAVEL', label: 'Renda Variável (Ações, BDRs)' },
    { value: 'FUNDO_IMOBILIARIO', label: 'Fundo Imobiliário (FII)' },
    { value: 'TESOURO_DIRETO', label: 'Tesouro Direto' },
    { value: 'CRIPTOATIVO', label: 'Criptoativo' },
]);

const sortedInvestimentos = computed(() => {
    const list = Array.isArray(investimentos.value) ? investimentos.value : [];
    return [...list].sort((a, b) => (b.valorAtual || 0) - (a.valorAtual || 0));
});
const totalInvestimentos = computed(() => {
    const list = Array.isArray(investimentos.value) ? investimentos.value : [];
    return list.reduce((total, item) => total + (parseFloat(item.valorAtual) || 0), 0);
});
const showTickerInput = computed(() => {
    if (!investimento.value) return false;
    const tickerTypes = ['RENDA_VARIAVEL', 'FUNDO_IMOBILIARIO'];
    return tickerTypes.includes(investimento.value.tipo);
});
const isFormDirty = computed(() => {
    if (!pristineInvestimento.value || !investimento.value) return false;
    return JSON.stringify(investimento.value) !== JSON.stringify(pristineInvestimento.value);
});

watch(investimento, (newVal) => {
    if (newVal && !showTickerInput.value) { newVal.ticker = ''; }
}, { deep: true });

const authConfig = () => {
    const token = localStorage.getItem('token');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}
const fetchInvestimentos = async () => {
    try {
        const response = await axios.get(apiUrl, authConfig());
        investimentos.value = response.data || [];
    } catch (error) {
        console.error('Erro ao buscar investimentos:', error);
        investimentos.value = [];
    }
};

const handleNovoInvestimento = () => {
    if (isMobile.value) {
        router.push({ name: 'InvestimentosAdd' });
    } else {
        showAddInvestimentoModal();
    }
};

const handleEditInvestimento = (itemToEdit) => {
    if (isMobile.value) {
        router.push({ name: 'InvestimentosEdit', params: { id: itemToEdit.id } });
    } else {
        editInvestimento(itemToEdit);
    }
};

const handleSubmit = async () => {
    if (isSubmitting.value) return;
    isSubmitting.value = true;
    try {
        const payload = { ...investimento.value, ticker: investimento.value.ticker?.toUpperCase() };
        if (isEditing.value) {
            await axios.put(`${apiUrl}/${payload.id}`, payload, authConfig());
        } else {
            await axios.post(apiUrl, payload, authConfig());
        }
        await fetchInvestimentos();
        forceCloseModal();
    } catch (error) {
        console.error(`Erro ao salvar investimento:`, error.response?.data || error.message);
        alert('Erro ao salvar investimento.');
    } finally {
        isSubmitting.value = false;
    }
};
const executeDelete = async () => {
    if (itemParaDeletar.value === null) return;
    try {
        await axios.delete(`${apiUrl}/${itemParaDeletar.value}`, authConfig());
        await fetchInvestimentos();
    } catch (error) { console.error('Erro ao excluir investimento:', error); }
    finally {
        showDeleteConfirm.value = false;
        itemParaDeletar.value = null;
    }
};

const getInitialFormData = () => ({ id: null, descricao: '', tipo: 'RENDA_FIXA', instituicao: '', ticker: '', valorAtual: null });
const resetForm = () => {
    investimento.value = getInitialFormData();
    isEditing.value = false;
    pristineInvestimento.value = null;
};
const showAddInvestimentoModal = () => {
    resetForm();
    pristineInvestimento.value = JSON.parse(JSON.stringify(investimento.value));
    showModal.value = true;
};
const editInvestimento = (itemToEdit) => {
    investimento.value = { ...itemToEdit };
    pristineInvestimento.value = { ...itemToEdit };
    isEditing.value = true;
    showModal.value = true;
};
const confirmDelete = (id) => {
    itemParaDeletar.value = id;
    showDeleteConfirm.value = true;
};
const closeModal = () => {
    if (isSubmitting.value) return;
    if (isFormDirty.value) {
        showUnsavedChangesConfirm.value = true;
    } else {
        forceCloseModal();
    }
};
const forceCloseModal = () => {
    showModal.value = false;
    resetForm();
};
const handleConfirmClose = () => {
    showUnsavedChangesConfirm.value = false;
    forceCloseModal();
};

const toggleVisibility = () => {
    showValues.value = !showValues.value;
    localStorage.setItem('showFinancialValues', JSON.stringify(showValues.value));
};
const formatCurrency = (value) => {
    if (typeof value !== 'number') return '0,00';
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const displayCurrency = (value, showSymbol = true) => {
    if (showValues.value) {
        const formattedValue = formatCurrency(value);
        return showSymbol ? `R$ ${formattedValue}` : formattedValue;
    }
    return showSymbol ? 'R$ ●●●●●' : '●●●●●';
};
const calculatePercentage = (value) => {
    if (totalInvestimentos.value === 0 || typeof value !== 'number') return '0,00%';
    const percentage = (value / totalInvestimentos.value) * 100;
    return `${percentage.toFixed(2)}%`.replace('.', ',');
}
const getTipoLabel = (tipoValue) => {
    const tipo = tiposInvestimento.value.find(t => t.value === tipoValue);
    return tipo ? tipo.label.split(' (')[0] : tipoValue;
};
const getInvestmentTheme = (tipoValue) => {
    return investmentThemes[tipoValue] || investmentThemes.CRIPTOATIVO;
};
const getIconForTipo = (tipoValue) => {
    const icons = {
        RENDA_FIXA: 'database',
        RENDA_VARIAVEL: 'trending_up',
        FUNDO_IMOBILIARIO: 'real_estate_agent',
        TESOURO_DIRETO: 'account_balance',
        CRIPTOATIVO: 'currency_bitcoin',
    };
    return icons[tipoValue] || 'monetization_on';
}

onMounted(() => {
    const savedVisibility = localStorage.getItem('showFinancialValues');
    if (savedVisibility !== null) {
        showValues.value = JSON.parse(savedVisibility);
    }
    resetForm();
    fetchInvestimentos();
    checkDevice();
    window.addEventListener('resize', checkDevice);
});

onUnmounted(() => {
    window.removeEventListener('resize', checkDevice);
});
</script>

<style scoped>
.form-label {
    @apply block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1;
}

.form-input {
    @apply w-full px-3 py-2 text-sm bg-white dark:bg-slate-800/50 border border-solid border-slate-300 dark:border-slate-700 rounded-lg transition ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-500 text-slate-800 dark:text-slate-200;
}

.btn-primary {
    @apply px-5 py-2.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-brand-500/20 hover:shadow-brand-500/30;
}

.btn-secondary {
    @apply px-5 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-lg transition-colors duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed;
}

.btn-danger {
    @apply px-5 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors duration-150 ease-in-out;
}

.btn-action-icon {
    @apply p-1.5 rounded-full transition-colors duration-200;
}

.card-investment {
    @apply bg-white dark:bg-brand-800 shadow-lg rounded-2xl flex flex-col overflow-hidden transition-transform hover:-translate-y-1 duration-300;
}

.card-footer {
    @apply mt-auto p-2 bg-slate-50 dark:bg-brand-900/40 flex items-center justify-end space-x-2 border-t border-slate-100 dark:border-brand-700/50;
}

.empty-state {
    @apply text-center py-16 text-slate-500 dark:text-slate-400 bg-white dark:bg-brand-800 rounded-xl shadow-md flex flex-col justify-center items-center;
}

.modal-backdrop {
    @apply fixed inset-0 z-50 flex items-center justify-center bg-black/60 dark:bg-brand-900/80 backdrop-blur-sm p-4;
}

.modal-card {
    @apply bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-md transform transition-all;
}

.modal-header {
    @apply px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center;
}

.modal-close-btn {
    @apply p-1 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed;
}

.modal-footer {
    @apply px-4 py-3 bg-slate-50 dark:bg-slate-800/50 flex justify-end space-x-3 rounded-b-xl border-t border-slate-200 dark:border-slate-800;
}

.modal-title {
    @apply mt-2 text-xl font-semibold text-slate-800 dark:text-slate-100;
}

.modal-text {
    @apply mt-2 text-sm text-slate-600 dark:text-slate-400;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
    opacity: 0;
    transform: scale(0.95) translateY(-20px);
}

.animate-spin {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}
</style>